import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DollarSign, PlusCircle, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? '—' : `${(Number(v) * 100).toFixed(2)}%`);
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const STATUS_CLS = {
  'Pricing Approved': 'bg-[#8FC93A]/20 text-[#4d7c0f] border-[#8FC93A]',
  'Awaiting Pricing Approval': 'bg-amber-100 text-amber-700 border-amber-300',
  'Pricing Pending': 'bg-slate-100 text-slate-600 border-slate-300',
  'Pricing Needs Revision': 'bg-red-50 text-red-600 border-red-200',
  'No Budget': 'bg-slate-50 text-slate-400 border-slate-200',
};

// Internal Tag-Work pricing on a ROUT work order. Operational status is separate:
// a completed work order may still show Pricing Pending here.
export default function TagWorkPricingCard({ workOrderId }) {
  const { token } = useContext(AuthContext);
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/tagwork-budgets/for-work-order/${workOrderId}`, auth(token)).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [workOrderId, token]);
  useEffect(() => { load(); }, [load]);
  if (!d || d.error) return null;

  const openTally = (twbId) => window.open(`/contract-budgets?tab=tagwork${twbId ? `&twb=${twbId}` : ''}`, '_blank', 'noopener');
  const createBudget = async () => {
    try {
      const r = await axios.post(`${API}/tagwork-budgets/create-from-work-order/${workOrderId}`, {}, auth(token));
      toast.success(`Budget ${r.data.reference_number} created`);
      openTally(r.data.id); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create budget'); load(); }
  };

  const b = d.budget;
  return (
    <div className="rounded-xl border border-[#7C3AED]/30 bg-white p-4" data-testid="wo-tagwork-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-bold text-[#7C3AED]"><DollarSign size={15} />Tag-Work Pricing</h3>
        <span data-testid="wo-tagwork-status" className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[d.pricing_status] || STATUS_CLS['No Budget']}`}>{d.pricing_status}</span>
      </div>

      {/* Approved amount is visible to any work-order viewer */}
      {d.approved_amount != null && (
        <div className="mb-2 flex items-center gap-1.5 text-sm" data-testid="wo-tagwork-approved">
          <CheckCircle2 size={14} className="text-[#4d7c0f]" />
          <span className="font-semibold text-[#0E2A47]">Approved selling amount {money(d.approved_amount)}</span>
          {d.approved_revision ? <span className="text-slate-400">· rev {d.approved_revision}</span> : null}
          {d.ready_for_billing && <span className="ml-1 rounded bg-[#8FC93A]/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#4d7c0f]">Ready for Billing</span>}
        </div>
      )}
      {d.reference_number && <div className="mb-2 font-mono text-xs text-slate-500" data-testid="wo-tagwork-ref">{d.reference_number}</div>}

      {/* Internal cost breakdown — only for Tag-Work viewers */}
      {d.can_view_costs && b && (
        <div className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs" data-testid="wo-tagwork-breakdown">
          <span className="text-slate-500">Direct cost</span><span className="text-right font-medium">{money(b.direct_cost)}</span>
          <span className="text-slate-500">Overhead</span><span className="text-right font-medium">{money(b.overhead_amount)}</span>
          <span className="text-slate-500">Total estimated cost</span><span className="text-right font-medium">{money(b.total_estimated_cost)}</span>
          <span className="text-slate-500">Proposed selling price</span><span className="text-right font-semibold text-[#7C3AED]">{money(b.selling_price)}</span>
          <span className="text-slate-500">Profit margin</span><span className={`text-right font-medium ${b.below_min_profitability ? 'text-red-600' : 'text-[#4d7c0f]'}`}>{pct(b.profit_margin_pct)}{b.below_min_profitability ? ' ⚠' : ''}</span>
        </div>
      )}
      {!d.can_view_costs && d.has_budget && (
        <p className="mb-2 text-xs text-slate-400" data-testid="wo-tagwork-costs-hidden">Internal cost detail is restricted. You can see the approved selling amount and pricing status.</p>
      )}

      <div className="flex flex-wrap gap-2">
        {d.has_budget ? (
          <Button size="sm" variant="outline" onClick={() => openTally(b?.id)} data-testid="wo-tagwork-open-btn" disabled={!d.can_view_costs}><ExternalLink size={14} className="mr-1.5" />Open Linked Budget</Button>
        ) : (
          d.can_manage && <Button size="sm" onClick={createBudget} data-testid="wo-tagwork-create-btn" className="bg-[#7C3AED] text-white hover:bg-[#6d28d9]"><PlusCircle size={14} className="mr-1.5" />Create Budget</Button>
        )}
      </div>
      <p className="mt-2 flex items-start gap-1 text-[11px] text-slate-400"><AlertTriangle size={11} className="mt-0.5 shrink-0" />Pricing is separate from operational status — work can proceed and complete before pricing is entered or approved. Internal pricing approval is not customer proposal approval.</p>
    </div>
  );
}
